import { useState, useEffect } from 'react';
import { Button, Input, Select, Stack, Text, createListCollection } from '@chakra-ui/react';
import { useMap } from '../map/map-context';
import { fromLonLat } from 'ol/proj';
import utmObj from 'utm-latlng';
import { StorageManager } from '@/lib/utils/storage-manager';

const storageManager = StorageManager.getInstance();

export default function UTMSearch({ onClose }: { onClose: () => void }) {
  const [zone, setZone] = useState('');
  const [easting, setEasting] = useState('');
  const [northing, setNorthing] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { map } = useMap();

  // Load saved UTM state on component mount
  useEffect(() => {
    const savedState = storageManager.loadUTMState();
    setZone(savedState.zone);
    setEasting(savedState.easting);
    setNorthing(savedState.northing);
  }, []);

  // Save UTM state whenever values change
  useEffect(() => {
    if (zone || easting || northing) {
      storageManager.saveUTMState(zone, easting, northing);
    }
  }, [zone, easting, northing]);

  const zoneCollection = createListCollection({
    items: [
      { label: '12 N', value: '12N' },
      { label: '13 N', value: '13N' },
      { label: '14 N', value: '14N' },
      { label: '12 U', value: '12U' },
      { label: '13 U', value: '13U' },
      { label: '14 U', value: '14U' },
      { label: '12 V', value: '12V' },
      { label: '13 V', value: '13V' },
      { label: '14 V', value: '14V' },
    ],
  });

  const handleSearch = async () => {
    if (!zone || !easting || !northing) {
      setError('Please fill in all fields');
      return;
    }

    const eastingNum = parseFloat(easting);
    const northingNum = parseFloat(northing);

    if (isNaN(eastingNum) || isNaN(northingNum)) {
      setError('Please enter valid numeric values');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const utm = new utmObj();
      const zoneNumber = parseInt(zone);
      const zoneLetter = zone.slice(-1);

      const result = utm.convertUtmToLatLng(eastingNum, northingNum, zoneNumber, zoneLetter);
      const latLng = typeof result === 'string' ? { lat: 0, lng: 0 } : result;

      if (map) {
        const view = map.getView();
        view.animate({
          center: fromLonLat([latLng.lng, latLng.lat]),
          zoom: 14,
          duration: 1000,
        });
        onClose();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to convert UTM coordinates');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap='4'>
      <Text fontSize='sm' color='gray.600'>
        Enter UTM coordinates
      </Text>

      <Stack gap='3'>
        <Select.Root
          collection={zoneCollection}
          value={[zone]}
          onValueChange={(e) => setZone(e.value[0])}
        >
          <Select.HiddenSelect />
          <Select.Label>UTM Zone</Select.Label>
          <Select.Control>
            <Select.Trigger>
              <Select.ValueText placeholder="Select zone" />
            </Select.Trigger>
            <Select.IndicatorGroup>
              <Select.Indicator />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Content>
            {zoneCollection.items.map((item) => (
              <Select.Item key={item.value} item={item}>
                {item.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <div>
          <Text fontSize='sm' mb='1'>
            Easting
          </Text>
          <Input
            type='number'
            placeholder='Easting'
            min={48}
            max={60}
            value={easting}
            onChange={(e) => setEasting(e.target.value)}
          />
        </div>

        <div>
          <Text fontSize='sm' mb='1'>
            Northing
          </Text>
          <Input
            type='number'
            placeholder='Northing'
            min={48}
            max={60}
            value={northing}
            onChange={(e) => setNorthing(e.target.value)}
          />
        </div>
      </Stack>

      {error && (
        <Text color='red.500' fontSize='sm'>
          {error}
        </Text>
      )}

      <Button onClick={handleSearch} loading={loading} disabled={!zone || !easting || !northing}>
        Search UTM
      </Button>
    </Stack>
  );
}
